/**
 * Viewer vocabulary shared by the canvas, the grid, the View tab and the panels.
 *
 * The plane names are strings rather than NiiVue's `SLICE_TYPE` enum on purpose:
 * the enum only exists once `@niivue/niivue` has been dynamically imported, and
 * every module here must be usable before (or without) that import succeeding.
 */

/** What a single viewport pane renders. `render` is NiiVue volume rendering. */
export type ViewPlane = "axial" | "coronal" | "sagittal" | "multiplanar" | "render";

export const PLANE_LABELS: Record<ViewPlane, string> = {
  axial: "Axial",
  coronal: "Coronal",
  sagittal: "Sagittal",
  multiplanar: "Multiplanar",
  render: "3D",
};

/** Edge letters drawn around a 2D pane, in [top, bottom, left, right] order. */
export const PLANE_EDGE_LETTERS: Record<ViewPlane, [string, string, string, string]> = {
  // Neurological convention (NiiVue's default): subject-left on the image right.
  axial: ["A", "P", "R", "L"],
  coronal: ["S", "I", "R", "L"],
  sagittal: ["S", "I", "A", "P"],
  multiplanar: ["", "", "", ""],
  render: ["", "", "", ""],
};

export type LayoutMode = "single" | "2x2" | "mpr" | "compare";

/** Number of panes each layout shows; the grid slices `panes` to this length. */
export const LAYOUT_PANE_COUNT: Record<LayoutMode, number> = {
  single: 1,
  "2x2": 4,
  mpr: 3,
  compare: 2,
};

export type ViewerStatus = "empty" | "loading" | "ready" | "error";

/** Measured from the volume NiiVue actually loaded — never from the manifest. */
export interface VolumeStats {
  /** Raw voxel extremes over the loaded frame. */
  min: number;
  max: number;
  /** Display window currently applied. */
  calMin: number;
  calMax: number;
  /** Voxels strictly greater than zero; the honest denominator for a mask. */
  nonZeroVox: number;
  /** nonZeroVox × voxel volume, in millilitres. */
  volumeMl: number;
  /** |dx × dy × dz| in mm³. */
  voxelMm3: number;
  /** Finite voxels in the first frame (intensity stats use these). */
  finiteVox: number;
  mean: number;
  /** Sample standard deviation. */
  std: number;
  median: number | null;
  p5: number | null;
  p95: number | null;
  /** Centre of mass of voxels > 0, in voxel index space. */
  centroidVox: [number, number, number] | null;
  /** Inclusive bounding box of voxels > 0. */
  bboxVox: { min: [number, number, number]; max: [number, number, number] } | null;
  dims: number[];
  pixDims: number[];
}

/** Base-image intensities inside the overlay mask. Only when both share a grid. */
export interface MaskedIntensity {
  compatible: boolean;
  voxels: number;
  mean: number | null;
  std: number | null;
  min: number | null;
  max: number | null;
  backgroundMean: number | null;
  backgroundStd: number | null;
}

export interface ViewerStatsPayload {
  base: VolumeStats | null;
  overlay: VolumeStats | null;
  masked: MaskedIntensity | null;
}

export interface LocationInfo {
  vox: [number, number, number];
  mm: [number, number, number];
  /** NiiVue's own formatted string, e.g. "-12x34x8  1234". */
  label: string;
  values: { name: string; value: number }[];
}

/** Colormaps NiiVue ships that make sense for greyscale anatomy / heat overlays. */
export const BASE_COLORMAPS = ["gray", "bone", "plasma", "viridis", "inferno"] as const;
export const OVERLAY_COLORMAPS = ["warm", "hot", "red", "winter", "cool", "jet"] as const;

export const OVERLAY_COLORMAP_LABELS: Record<string, string> = {
  warm: "Warm (Red-Yellow)",
  hot: "Hot (Red-Yellow)",
  red: "Red",
  winter: "Winter (Blue-Green)",
  cool: "Cool",
  jet: "Jet",
};
